import { Injectable } from '@angular/core';
import { DraftService, Order, OrderItem, OrdersService, OrderTypeCodesEnum } from '@cemex-core/cmx-api';
import { CreateDraftResponse } from '@cemex-core/cmx-api/models/drafts/responses/create-draft-response';
import { CmxClientService } from '@cemex/cmx-client-layout';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ONE_CLICK_PROFILE_CONSTANTS } from '@ordersnproducts/modules/one-click-order/constants/one-click-profile-state';
import { OneClickDraftService } from '@ordersnproducts/modules/one-click-order/services/one-click-draft.service';
import { of, throwError as observableThrowError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  OneClickCreateProfileCompleteAction,
  OneClickLoadOrderDetailErrorAction,
  OneClickLoadOrderDetailStartAction,
  OneClickLoadOrderDetailSuccessAction,
  OneClickProfileActionTypes,
} from '../actions/one-click-profile.actions';
import { getOneClickState, OneClickState, State } from '../reducers';
import { oneClickStateCustomer } from '../selectors/one-click-profiles.selector';
import { getProductId } from './../../order-builder/modules/specification/helpers/product.helper';

@Injectable()
export class OneClickProfileEffects {
  createOneClickProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickProfileActionTypes.CREATE_PROFILE),
      switchMap(() => this.store.pipe(select(oneClickStateCustomer), take(1))),
      map((state) =>
        this.oneClickDraftService.createRequest(
          state.oneClick,
          state.customer,
          ONE_CLICK_PROFILE_CONSTANTS.orderTypeCode,
        ),
      ),
      switchMap((request) => this.draftService.createDraftV6(request)),
      map((response: CreateDraftResponse) => new OneClickCreateProfileCompleteAction(response)),
      catchError((error) => {
        const errorMessage = error && error.error && error.error.moreInformation;
        if (errorMessage) {
          this.alertService.addMainAlert('danger', this.translate.instant(errorMessage));
          return observableThrowError(errorMessage);
        } else {
          return observableThrowError(null);
        }
      }),
    ),
  );

  updateOneClickProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickProfileActionTypes.UPDATE_PROFILE),
      switchMap(() => this.store.pipe(select(getOneClickState), take(1))),
      switchMap((state) =>
        this.ordersService.updateOrderV6(
          state.profileState.presetOrder.orderId,
          this.updatePreload(state.profileState.presetOrder, state),
        ),
      ),
      map((response: CreateDraftResponse) => new OneClickCreateProfileCompleteAction(response)),
    ),
  );

  getOneClickOrderDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickProfileActionTypes.LOAD_PROFILE_DETAIL),
      switchMap(({ payload }: OneClickLoadOrderDetailStartAction) =>
        this.ordersService.getOrderByIdV6(payload, OrderTypeCodesEnum.OneClickToBuy).pipe(
          switchMap((response: Order) => [new OneClickLoadOrderDetailSuccessAction(response)]),
          catchError((error) => of(new OneClickLoadOrderDetailErrorAction(error))),
        ),
      ),
    ),
  );

  private updatePreload(order: Order, state: OneClickState) {
    const modded = { ...order };
    modded.items = order.items.map((item, index) => {
      return this.updateOrderItem(item, state, index);
    });
    return modded;
  }

  private updateOrderItem(item: OrderItem, state: OneClickState, index: number) {
    const modded = { ...item };

    // Mod product
    modded.product = {
      ...modded.product,
      productId: getProductId(state.productsState.selected[0]),
    };

    // Mod payment term
    if (state.paymentsState.selected) {
      modded.paymentTerm = {
        ...modded.paymentTerm,
        paymentTermId: state.paymentsState.selected
          ? Number(state.paymentsState.selected)
          : modded.paymentTerm.paymentTermId,
      };
    }

    // Quantity
    if (state.quantityState.value.length) {
      modded.quantity = state.quantityState.value[index]
        ? state.quantityState.value[index]
        : state.quantityState.value[0];
    }

    // Unit
    if (state.unitsSate.selected) {
      modded.uom = {
        ...modded.uom,
        unitId: this.oneClickDraftService.retrieveUnit(state),
      };
    }

    // Mod plant
    if (state.plansState.selected) {
      modded.shippingSource = {
        ...modded.shippingSource,
        shippingSourceId: Number(state.plansState.selected),
      };
    }

    return modded;
  }

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private draftService: DraftService,
    private ordersService: OrdersService,
    private alertService: CmxClientService,
    private translate: TranslateService,
    private oneClickDraftService: OneClickDraftService,
  ) {}
}
